import React from 'react';
import { Box, Typography, Button, styled, Alert } from '@mui/material';
import { Question } from '../../types';

const OptionButton = styled(Button)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 'normal',
  justifyContent: 'center',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  '&.selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
}));

interface Props {
  question: Question;
  booleanValue: boolean | null;
  onChange: (value: boolean) => void;
  showFeedback?: boolean;
}

export const StyledBooleanQuestion: React.FC<Props> = ({ 
  question, 
  booleanValue, 
  onChange,
  showFeedback = false
}) => {
  // Get custom true/false text from question
  const trueText = question.high_text || 'True';
  const falseText = question.low_text || 'False';
  
  // Check if answer is correct if feedback is enabled
  const isCorrect = showFeedback && booleanValue !== null ? 
    booleanValue === question.correct_boolean_value : 
    undefined;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 2,
        width: '100%'
      }}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: 2,
          width: '100%'
        }}>
          <OptionButton
            variant="outlined"
            className={booleanValue === true ? 'selected' : ''}
            onClick={() => onChange(true)}
          >
            {trueText}
          </OptionButton>
          <OptionButton
            variant="outlined"
            className={booleanValue === false ? 'selected' : ''}
            onClick={() => onChange(false)}
          >
            {falseText}
          </OptionButton>
        </Box>
        
        {showFeedback && booleanValue !== null && (
          <Box sx={{ mt: 2 }}>
            <Alert 
              severity={isCorrect ? 'success' : 'error'}
              sx={{ mb: question.explanation ? 2 : 0 }}
            >
              {isCorrect ? 'Correct!' : 'Incorrect!'}
            </Alert>
            {question.explanation && (
              <Alert severity="info">
                {question.explanation}
              </Alert>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
